<template>
    <section
        :id="blok.form ? `article-cta-${blok.form}` : undefined"
        v-editable="blok"
        class="article-cta"
    >
        <!-- karibu owns no page layout, so the section stays for the margin and bleed. -->
        <KContentBlock
            class="article-cta__block"
            as="div"
            :title="blok.title"
            :variant="cardVariant"
            padding="md"
            :aside-position="blok.image_position === 'left' ? 'start' : 'end'"
        >
            <!-- v-if here removes the slot entirely, so KContentBlock drops its wrapper and gap. -->
            <template
                v-if="blok.description"
                #default
            >
                <!-- Repeat .k-prose: this div, not the paragraphs, is the child karibu spaces. -->
                <div
                    class="k-prose"
                    v-html="renderRichText(blok.description)"
                />
            </template>

            <template
                v-if="blok.image?.filename"
                #aside
            >
                <KFrame
                    ratio="4 / 3"
                    rounded
                >
                    <NuxtImg
                        :src="blok.image.filename"
                        :alt="blok.image.alt || blok.title || ''"
                        :width="imageSize?.width"
                        :height="imageSize?.height"
                        fit="cover"
                        format="auto"
                        loading="lazy"
                        sizes="xs:100vw sm:100vw md:400px"
                    />
                </KFrame>
            </template>

            <template #actions>
                <KButton
                    :label="blok.button_text"
                    @click="handleButtonClick"
                >
                    <template
                        v-if="blok.button_icon"
                        #icon-end
                    >
                        <Icon :name="blok.button_icon" />
                    </template>
                </KButton>
            </template>
        </KContentBlock>

        <ArticleCtaDialog
            v-if="formData"
            v-model:open="showDialog"
            :definition="formData"
            :form-id="blok.form"
            @submitted="closeDialog"
        />
    </section>
</template>

<script setup>
import { KButton, KContentBlock, KFrame } from '@vemedia/karibu'
import storyblokImageSize from '~/helpers/storyblokImageSize'

const props = defineProps({
    blok: {
        type: Object,
        required: true,
    },
})

// Deferred: the dialog drags in KDialog, StoryblokForm and with them the
// date-picker/TanStack graph. The CTA is a click away and renders no dialog
// markup during SSR (v-if="formData" is false until the form is fetched).
const importDialog = () => import('~/components/article-cta-dialog/article-cta-dialog.vue')
const ArticleCtaDialog = defineAsyncComponent(importDialog)

const showDialog = ref(false)
const formData = ref(null)

// Storyblok's variant names predate karibu; these are the KCard surfaces they map onto.
const CARD_VARIANTS = {
    primary: 'accent',
    secondary: 'subtle',
    outline: 'outline',
}

const cardVariant = computed(() => CARD_VARIANTS[props.blok.variant] ?? 'subtle')

// The KFrame above displays this at 4/3, so request a 4/3-capped transform
// (fit=cover crops to it) rather than the intrinsic ratio. Cap the width at
// the upload's, so no variant upscales. The attrs also reserve layout —
// though the frame's aspect-ratio already does that.
const FRAME_RATIO = 4 / 3

const imageSize = computed(() => {
    const dims = storyblokImageSize(props.blok.image?.filename)
    if (!dims)
        return null

    const height = Math.min(dims.height, Math.round(dims.width / FRAME_RATIO))

    return {
        width: Math.round(height * FRAME_RATIO),
        height,
    }
})

// Fetch form data if form is specified
async function fetchFormData() {
    if (!props.blok.form)
        return

    try {
        const data = await $fetch(`/api/form-embed?uuid=${props.blok.form}`)
        formData.value = data
    } catch (error) {
        console.error('Error fetching form:', error)
    }
}

// Check if the same form exists on the page
function findExistingFormOnPage() {
    if (!props.blok.form)
        return null

    // Look for form-embed elements with the same form UUID using data attribute
    const formId = props.blok.form
    const selector = `.form-embed__wrapper[data-form-uuid="${formId}"]`
    const existingForm = document.querySelector(selector)

    return existingForm
}

// Handle button click
async function handleButtonClick() {
    if (!props.blok.form) {
        console.warn('ArticleCTA: No form configured for this CTA')
        return
    }

    // Small delay to ensure DOM is fully rendered (form-embed may still be loading)
    await new Promise(resolve => setTimeout(resolve, 100))

    // First check if form is already on the page
    const existingForm = findExistingFormOnPage()

    if (existingForm) {
        // Scroll to the existing form smoothly
        existingForm.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Add a highlight effect
        existingForm.classList.add('article-cta__highlight')
        setTimeout(() => {
            existingForm.classList.remove('article-cta__highlight')
        }, 2000)
    } else {
        // Fetch the definition and the dialog chunk together, so a first tap waits
        // for the slower of the two rather than for one after the other.
        await Promise.all([
            formData.value ? null : fetchFormData(),
            importDialog(),
        ])
        showDialog.value = true
    }
}

// Close dialog
function closeDialog() {
    showDialog.value = false
}

// Render rich text (Storyblok rich text format)
function renderRichText(richText) {
    if (!richText)
        return ''

    // If it's already HTML string, return it
    if (typeof richText === 'string')
        return richText

    // If it's a Storyblok rich text object, render it
    if (richText.content) {
        return richText.content.map((node) => {
            if (node.type === 'paragraph') {
                const text = node.content?.map(c => c.text || '').join('') || ''
                return `<p>${text}</p>`
            } else if (node.type === 'text') {
                return node.text || ''
            }
            return ''
        }).join('')
    }

    return ''
}
</script>

<style lang="scss">
@use 'article-cta';
</style>
